import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPinIcon, BedDoubleIcon, RulerIcon, BuildingIcon, PhoneIcon, MailIcon, UserIcon, ShareIcon, DownloadIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, CheckCircleIcon } from 'lucide-react';
// Mock data - in production this would come from API
const mockPresentationData = {
  property: {
    title: 'Apartament modern 3 camere - Zona Centrală',
    address: 'Strada Victoriei nr. 45',
    city: 'București',
    zone: 'Sector 1 - Centru',
    propertyType: 'Apartament',
    rooms: '3',
    surface: '85',
    floor: '4/8',
    price: '185000',
    description: 'Apartament spațios și luminos, complet renovat în 2023. Situat într-o zonă liniștită, cu acces facil la transport public și toate facilitățile necesare. Finisaje premium, bucătărie complet utilată, 2 băi moderne. Vedere panoramică spre parc.',
    features: ['Balcon', 'Parcare', 'Mobilat', 'Utilat', 'Centrală proprie', 'Aer condiționat', 'Lift']
  },
  agent: {
    name: 'Maria Popescu',
    phone: '0722 123 456',
    email: 'maria@cautlocuinta.ro',
    photo: null,
    tagline: 'Te ajut cu tot procesul de vânzare/închiriere',
    showContactHeader: true,
    showContactFooter: true
  },
  photos: [{
    id: '1',
    url: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1200'
  }, {
    id: '2',
    url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=1200'
  }, {
    id: '3',
    url: 'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=1200'
  }, {
    id: '4',
    url: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200'
  }, {
    id: '5',
    url: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200'
  }]
};
export function PropertyPresentationView() {
  const {
    presentationId
  } = useParams<{
    presentationId: string;
  }>();
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [copied, setCopied] = useState(false);
  const {
    property,
    agent,
    photos
  } = mockPresentationData;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const nextPhoto = () => {
    setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
  };
  const prevPhoto = () => {
    setCurrentPhotoIndex(prev => (prev - 1 + photos.length) % photos.length);
  };
  return <div className="min-h-screen bg-white" data-id="element-10407">
      {/* Header */}
      <header className="bg-[#132C53] text-white sticky top-0 z-40" data-id="element-10408">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4" data-id="element-10409">
          <div className="flex items-center justify-between" data-id="element-10410">
            <div className="flex items-center space-x-3" data-id="element-10411">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-10412">
                <span className="text-[#132C53] font-bold text-sm" data-id="element-10413">CLC</span>
              </div>
              <div data-id="element-10414">
                <span className="font-bold text-lg" data-id="element-10415">CautLocuinta.ro</span>
                <p className="text-xs text-white/70" data-id="element-10416">Prezentare Proprietate</p>
              </div>
            </div>
            {agent.showContactHeader && <div className="hidden sm:block text-right" data-id="element-10417">
                <p className="font-semibold" data-id="element-10418">{agent.name}</p>
                <p className="text-sm text-white/80" data-id="element-10419">{agent.phone}</p>
              </div>}
          </div>
        </div>
      </header>

      {/* Hero Section with Main Photo */}
      <section className="relative" data-id="element-10420">
        <div className="relative h-[50vh] sm:h-[60vh] cursor-pointer" onClick={() => setShowGallery(true)} data-id="element-10421">
          <img src={photos[0].url} alt={property.title} className="w-full h-full object-cover" data-id="element-10422" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" data-id="element-10423" />

          {/* Photo Counter */}
          <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium" data-id="element-10424">
            📷 {photos.length} fotografii
          </div>

          {/* Property Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 text-white" data-id="element-10425">
            <div className="max-w-6xl mx-auto" data-id="element-10426">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-10427">
                <h1 className="text-2xl sm:text-4xl font-bold mb-2" data-id="element-10428">
                  {property.title}
                </h1>
                <p className="text-lg text-white/90 flex items-center mb-4" data-id="element-10429">
                  <MapPinIcon className="w-5 h-5 mr-2" data-id="element-10430" />
                  {property.city}
                  {property.zone && `, ${property.zone}`}
                </p>
                <div className="flex items-center space-x-6 text-sm sm:text-base" data-id="element-10431">
                  {property.rooms && <span className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-10432">
                      <BedDoubleIcon className="w-4 h-4 mr-2" data-id="element-10433" />
                      {property.rooms} camere
                    </span>}
                  {property.surface && <span className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-10434">
                      <RulerIcon className="w-4 h-4 mr-2" data-id="element-10435" />
                      {property.surface} mp
                    </span>}
                  {property.floor && <span className="flex items-center bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full" data-id="element-10436">
                      <BuildingIcon className="w-4 h-4 mr-2" data-id="element-10437" />
                      Etaj {property.floor}
                    </span>}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-10438">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-10439">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-8" data-id="element-10440">
            {/* Price Card */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="bg-gradient-to-r from-[#357BEB] to-[#21A366] rounded-2xl p-6 text-white" data-id="element-10441">
              <p className="text-sm font-medium text-white/80 mb-1" data-id="element-10442">Preț</p>
              <p className="text-4xl sm:text-5xl font-bold" data-id="element-10443">
                €{Number(property.price).toLocaleString('ro-RO')}
              </p>
            </motion.div>

            {/* Photo Gallery Preview */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} data-id="element-10444">
              <h2 className="text-xl font-bold text-[#132C53] mb-4" data-id="element-10445">
                Galerie foto
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-10446">
                {photos.slice(0, 8).map((photo, index) => <button key={photo.id} onClick={() => {
                setCurrentPhotoIndex(index);
                setShowGallery(true);
              }} className="relative aspect-square rounded-xl overflow-hidden group" data-id="element-10447">
                    <img src={photo.url} alt="" className="w-full h-full object-cover transition-transform group-hover:scale-110" data-id="element-10448" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" data-id="element-10449" />
                  </button>)}
              </div>
            </motion.div>

            {/* Details Grid */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} data-id="element-10450">
              <h2 className="text-xl font-bold text-[#132C53] mb-4" data-id="element-10451">
                Detalii proprietate
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-10452">
                {[{
                label: 'Tip proprietate',
                value: property.propertyType,
                icon: '🏠'
              }, {
                label: 'Camere',
                value: property.rooms,
                icon: '🛏️'
              }, {
                label: 'Suprafață',
                value: `${property.surface} mp`,
                icon: '📐'
              }, {
                label: 'Etaj',
                value: property.floor,
                icon: '🏢'
              }].filter(item => item.value).map((item, idx) => <div key={idx} className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-10453">
                      <span className="text-2xl mb-2 block" data-id="element-10454">{item.icon}</span>
                      <p className="text-xs text-gray-500 mb-1" data-id="element-10455">{item.label}</p>
                      <p className="font-bold text-[#132C53]" data-id="element-10456">{item.value}</p>
                    </div>)}
              </div>
            </motion.div>

            {/* Description */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-10457">
              <h2 className="text-xl font-bold text-[#132C53] mb-4" data-id="element-10458">
                Descriere
              </h2>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-id="element-10459">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line" data-id="element-10460">
                  {property.description}
                </p>
              </div>
            </motion.div>

            {/* Features */}
            {property.features.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} data-id="element-10461">
                <h2 className="text-xl font-bold text-[#132C53] mb-4" data-id="element-10462">
                  Facilități
                </h2>
                <div className="flex flex-wrap gap-3" data-id="element-10463">
                  {property.features.map((feature, idx) => <span key={idx} className="px-4 py-2 bg-blue-50 text-[#357BEB] rounded-full font-medium border border-blue-100" data-id="element-10464">
                      ✓ {feature}
                    </span>)}
                </div>
              </motion.div>}
          </div>

          {/* Right Column - Agent & Actions */}
          <div className="space-y-6" data-id="element-10465">
            {/* Agent Card */}
            {agent.showContactFooter && <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden sticky top-24" data-id="element-10466">
                <div className="bg-gradient-to-r from-[#132C53] to-[#1e4278] p-6 text-white text-center" data-id="element-10467">
                  <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4 overflow-hidden" data-id="element-10468">
                    {agent.photo ? <img src={agent.photo} alt="" className="w-full h-full object-cover" data-id="element-10469" /> : <UserIcon className="w-10 h-10 text-white" data-id="element-10470" />}
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-10471">{agent.name}</h3>
                  {agent.tagline && <p className="text-sm text-white/80 mt-1" data-id="element-10472">
                      {agent.tagline}
                    </p>}
                </div>
                <div className="p-6 space-y-4" data-id="element-10473">
                  <a href={`tel:${agent.phone}`} className="flex items-center justify-center space-x-2 w-full py-3 bg-[#21A366] text-white rounded-xl font-semibold hover:bg-[#1a8555] transition-colors" data-id="element-10474">
                    <PhoneIcon className="w-5 h-5" data-id="element-10475" />
                    <span data-id="element-10476">{agent.phone}</span>
                  </a>
                  {agent.email && <a href={`mailto:${agent.email}`} className="flex items-center justify-center space-x-2 w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors" data-id="element-10477">
                      <MailIcon className="w-5 h-5" data-id="element-10478" />
                      <span data-id="element-10479">Trimite email</span>
                    </a>}
                </div>
              </motion.div>}

            {/* Share Actions */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="bg-gray-50 rounded-xl p-4 border border-gray-200" data-id="element-10480">
              <p className="text-sm font-semibold text-gray-700 mb-3" data-id="element-10481">
                Partajează
              </p>
              <div className="space-y-2" data-id="element-10482">
                <button onClick={handleCopyLink} className="flex items-center justify-center space-x-2 w-full py-2.5 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-10483">
                  {copied ? <>
                      <CheckCircleIcon className="w-4 h-4 text-green-500" data-id="element-10484" />
                      <span className="text-green-600" data-id="element-10485">Link copiat!</span>
                    </> : <>
                      <ShareIcon className="w-4 h-4" data-id="element-10486" />
                      <span data-id="element-10487">Copiază link</span>
                    </>}
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#132C53] text-white py-8" data-id="element-10488">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center" data-id="element-10489">
          <div className="flex items-center justify-center space-x-3 mb-4" data-id="element-10490">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center" data-id="element-10491">
              <span className="text-[#132C53] font-bold text-sm" data-id="element-10492">CLC</span>
            </div>
            <span className="font-bold text-lg" data-id="element-10493">CautLocuinta.ro</span>
          </div>
          <p className="text-sm text-white/70" data-id="element-10494">
            Prezentare generată automat • © {new Date().getFullYear()}{' '}
            CautLocuinta.ro
          </p>
        </div>
      </footer>

      {/* Fullscreen Gallery Modal */}
      {showGallery && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black z-50 flex items-center justify-center" data-id="element-10495">
          {/* Close Button */}
          <button onClick={() => setShowGallery(false)} className="absolute top-4 right-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10" data-id="element-10496">
            <XIcon className="w-6 h-6" data-id="element-10497" />
          </button>

          {/* Navigation */}
          <button onClick={prevPhoto} className="absolute left-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10" data-id="element-10498">
            <ChevronLeftIcon className="w-6 h-6" data-id="element-10499" />
          </button>
          <button onClick={nextPhoto} className="absolute right-4 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10" data-id="element-10500">
            <ChevronRightIcon className="w-6 h-6" data-id="element-10501" />
          </button>

          {/* Image */}
          <motion.img key={currentPhotoIndex} initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3
      }} src={photos[currentPhotoIndex].url} alt="" className="max-w-full max-h-full object-contain" data-id="element-10502" />

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium" data-id="element-10503">
            {currentPhotoIndex + 1} / {photos.length}
          </div>

          {/* Thumbnails */}
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex space-x-2" data-id="element-10504">
            {photos.map((photo, index) => <button key={photo.id} onClick={() => setCurrentPhotoIndex(index)} className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${index === currentPhotoIndex ? 'border-white opacity-100' : 'border-transparent opacity-50 hover:opacity-75'}`} data-id="element-10505">
                <img src={photo.url} alt="" className="w-full h-full object-cover" data-id="element-10506" />
              </button>)}
          </div>
        </motion.div>}
    </div>;
}